import { ArrowRightIcon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import TimePicker from '@/components/TimePicker';

/**
 * The hours of the day, as one fact.
 *
 * A planner does not hold two clock values; they hold "four till eleven" — a
 * start and a length. Two separately labelled time inputs made them state the
 * second value in the first one's terms with no help and no arithmetic, which is
 * why the end options here are anchored to the start and carry the duration, and
 * why the line underneath reads the pair back.
 *
 * Nothing reads the pair back underneath. The two fields already show what was
 * chosen, the end options carry the duration while the planner is picking, and
 * the wedding page states the hours a line below the couple's names — a fourth
 * copy in the dialog was noise.
 *
 * Either field can be filled first and neither waits for the other. An end with
 * no start is the one pair the schema refuses, so that state still says so
 * rather than being made unreachable — the planner who fills the fields out of
 * order gets a sentence, not a dead control, and not a save that fails with no
 * explanation.
 */

export default function WeddingHoursField({
  idPrefix,
  startTime,
  endTime,
  onChange,
}: {
  idPrefix: string;
  startTime: string;
  endTime: string;
  onChange: (patch: { startTime?: string; endTime?: string }) => void;
}) {
  const startId = `${idPrefix}-startTime`;
  const endId = `${idPrefix}-endTime`;

  return (
    <div className="space-y-2">
      <Label htmlFor={startId}>Hours</Label>

      <div className="flex items-center gap-2">
        <TimePicker
          id={startId}
          label="Start time"
          placeholder="Start"
          value={startTime}
          onChange={(startTime) => onChange({ startTime })}
        />
        <ArrowRightIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <TimePicker
          id={endId}
          label="End time"
          placeholder="End"
          value={endTime}
          after={startTime || undefined}
          onChange={(endTime) => onChange({ endTime })}
        />
      </div>

      {endTime && !startTime && (
        <p className="text-xs text-muted-foreground">A start time is needed alongside an end time.</p>
      )}
    </div>
  );
}
